import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Button, IconButton, SvgIcon, Tooltip } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import "./style.scss";
import { ReactComponent as CloseIcon } from "../../../assets/cross.icon.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/delete.icon.svg";
import { ReactComponent as EditIcon } from "../../../assets/edit.icon.svg";
import { ReactComponent as PlusIcon } from "../../../assets/plus.icon.svg";
import { ReactComponent as SearchIcon } from "../../../assets/search.icon.svg";
import {
    deleteIntegration,
    fetchIntegrations
} from "../../../api/admin/integrations/endpoints-referrers";
import { fetchKnowledgeBases } from "../../../api/admin/knowledge-base/endpoints-referrers";
import type { Integration } from "../../../common/interfaces/admin/integration.interface";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import HeaderLayout from "../../../components/layout/header-layout.comp";
import { LoaderView } from "../../../components/loader/loader.comp";
import { SelectionFilter } from "../../../components/menu-filter/menu-filter.comp";
import Toast, { ToastContainer } from "../../../components/toast/toast.comp";
import ConfirmationPopup, {
    ConfirmationPopupType
} from "../../../components/confirmation-popup/confirmation-popup.comp";
import type { KnowledgeBaseOption } from "./components/integration-basics-step.comp";
import IntegrationCreateModal from "./integration-create-modal.comp";
import IntegrationEditModal from "./integration-edit-modal.comp";

const sortOptions = ["Name", "Status"];

const getStatusInfo = (i: Integration): { label: string; color: string } => {
    if (i.isActive) return { label: "Active", color: "green" };
    if (i.authMode === "sso_redirect" && i.lastSsoTestOutcome !== "passed")
        return { label: "Needs setup", color: "orange" };
    if (i.authMode === "signed_identity" && !i.signedIdentityConfigured)
        return { label: "Needs setup", color: "orange" };
    return { label: "Inactive", color: "default" };
};

export default function IntegrationsListView() {
    const { user } = FirebaseAuthContainer.useContainer();
    const { showToast } = ToastContainer.useContainer();
    const [items, setItems] = useState<Integration[]>([]);
    const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBaseOption[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [searchString, setSearchString] = useState<string>("");
    const [selectedSort, setSelectedSort] = useState<string>("Name");
    const [createOpen, setCreateOpen] = useState<boolean>(false);
    const [editTarget, setEditTarget] = useState<Integration | null>(null);
    const [editInitialTab, setEditInitialTab] = useState<string | undefined>(undefined);
    const [deleteTarget, setDeleteTarget] = useState<Integration | null>(null);

    // Normal edits open on Basics; only the auto-open after create jumps to the
    // auth config tab (see handleCreated).
    const openEdit = (item: Integration) => {
        setEditInitialTab(undefined);
        setEditTarget(item);
    };

    const companyId = user?.companyId;

    const reload = useCallback(async () => {
        if (!companyId) return;
        setIsLoading(true);
        try {
            const { data } = await fetchIntegrations(companyId);
            setItems(Array.isArray(data) ? data : (data?.items ?? []));
        } finally {
            setIsLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        if (!companyId) return;
        fetchKnowledgeBases(companyId)
            .then(({ data }) => {
                const kbs = Array.isArray(data) ? data : (data?.items ?? data?.results ?? []);
                setKnowledgeBases(
                    kbs.map((kb: any) => ({
                        uid: kb.uid,
                        label: kb.displayName || kb.name || kb.uid
                    }))
                );
            })
            .catch(() => setKnowledgeBases([]));
    }, [companyId]);

    const filteredItems = useMemo(() => {
        const q = searchString.trim().toLowerCase();
        const list = q ? items.filter(i => i.name.toLowerCase().includes(q)) : items;
        return [...list].sort((a, b) => {
            if (selectedSort === "Status") {
                return getStatusInfo(a).label.localeCompare(getStatusInfo(b).label);
            }
            return a.name.localeCompare(b.name);
        });
    }, [items, searchString, selectedSort]);

    const upsert = (updated: Integration) => {
        setItems(prev => prev.map(i => (i.id === updated.id ? updated : i)));
        // Keep the open edit modal in sync with the refreshed row.
        setEditTarget(prev => (prev && prev.id === updated.id ? updated : prev));
    };

    const handleCreated = (created: Integration) => {
        setCreateOpen(false);
        setItems(prev => [...prev, created]);
        showToast("Integration created.", 3000);
        // Open it straight away so SSO / signed-identity / snippet can be set up.
        // For SSO (SAML/OIDC) or signed-identity, land on the config tab so the
        // IdP details / secret can be entered immediately.
        setEditInitialTab(
            created.authMode === "sso_redirect"
                ? "sso"
                : created.authMode === "signed_identity"
                  ? "signed"
                  : undefined
        );
        setEditTarget(created);
    };

    const handleDelete = async () => {
        if (!companyId || !deleteTarget) return;
        try {
            await deleteIntegration(companyId, deleteTarget.id);
            setItems(prev => prev.filter(i => i.id !== deleteTarget.id));
            showToast("Integration deleted.", 3000);
        } finally {
            // If we were deleting the row open in the edit modal, close it.
            setEditTarget(prev => (prev && prev.id === deleteTarget.id ? null : prev));
            setDeleteTarget(null);
        }
    };

    const handleSearchChange = (evt: ChangeEvent<HTMLInputElement>) => {
        setSearchString(evt.target.value ?? "");
    };
    const clearSearchString = () => setSearchString("");

    const showEmpty = !isLoading && filteredItems.length === 0;

    return (
        <HeaderLayout blurHeader={false}>
            <div className="embed-integrations">
                <div className="embed-integrations-title">
                    <span className="embed-integrations-title__text" role="heading" aria-level={1}>
                        Embed Integrations
                    </span>
                    <div className="embed-integrations-title-search">
                        <SvgIcon
                            className="embed-integrations-title-search__icon"
                            component={SearchIcon}
                            inheritViewBox
                        />
                        <input
                            type="text"
                            aria-label="Search"
                            placeholder="Search"
                            onChange={handleSearchChange}
                            value={searchString}
                        />
                        {!!searchString && (
                            <IconButton
                                className="embed-integrations-title-search__button--clear"
                                onClick={clearSearchString}
                            >
                                <CloseIcon />
                            </IconButton>
                        )}
                    </div>
                </div>
                <div className="embed-integrations-filters">
                    <div className="embed-integrations-filter embed-integrations-filter--sorting">
                        <SelectionFilter
                            list={sortOptions.map(o => `Sort by: ${o}`)}
                            selectedItem={`Sort by: ${selectedSort}`}
                            setSelectedItem={(val: string) =>
                                setSelectedSort(val.replace("Sort by: ", ""))
                            }
                            rightPosition={true}
                            minWidth={200}
                        />
                    </div>
                    <Button
                        className="embed-integrations-create-button"
                        variant="text"
                        startIcon={
                            <SvgIcon
                                className="embed-integrations-create-button__icon"
                                component={PlusIcon}
                                inheritViewBox
                            />
                        }
                        onClick={() => setCreateOpen(true)}
                    >
                        Create Integration
                    </Button>
                </div>
                <div className="embed-integrations-table-wrapper">
                    {showEmpty ? (
                        <div className="embed-integrations-empty">
                            {searchString
                                ? "No integrations match your search."
                                : "No integrations configured yet. Click 'Create Integration' to add one."}
                        </div>
                    ) : (
                        <div
                            className="embed-integrations-table"
                            role="table"
                            aria-label="Embed integrations"
                        >
                            <div className="embed-integrations-table-header" role="row">
                                <div
                                    className="embed-integrations-table-cell embed-integrations-table-cell__name"
                                    role="columnheader"
                                >
                                    Name
                                </div>
                                <div
                                    className="embed-integrations-table-cell embed-integrations-table-cell__surfaces"
                                    role="columnheader"
                                >
                                    Surfaces
                                </div>
                                <div
                                    className="embed-integrations-table-cell embed-integrations-table-cell__status"
                                    role="columnheader"
                                >
                                    Status
                                </div>
                                <div
                                    className="embed-integrations-table-cell embed-integrations-table-cell__action"
                                    role="columnheader"
                                >
                                    Actions
                                </div>
                            </div>
                            <div className="embed-integrations-table-body" role="rowgroup">
                                {filteredItems.map(item => {
                                    const status = getStatusInfo(item);
                                    return (
                                        <div
                                            key={item.id}
                                            className="embed-integrations-table-row"
                                            role="row"
                                        >
                                            <div
                                                className="embed-integrations-table-cell embed-integrations-table-cell__name"
                                                role="rowheader"
                                            >
                                                <span className="embed-integrations-cell-text">
                                                    {item.name}
                                                </span>
                                            </div>
                                            <div
                                                className="embed-integrations-table-cell embed-integrations-table-cell__surfaces"
                                                role="cell"
                                            >
                                                {item.linkEnabled && (
                                                    <span className="embed-integrations-surface-badge">
                                                        Link
                                                    </span>
                                                )}
                                                {item.widgetEnabled && (
                                                    <span className="embed-integrations-surface-badge">
                                                        Widget
                                                    </span>
                                                )}
                                            </div>
                                            <div
                                                className="embed-integrations-table-cell embed-integrations-table-cell__status"
                                                role="cell"
                                            >
                                                <span
                                                    className={`embed-integrations-status embed-integrations-status--${status.color}`}
                                                >
                                                    {status.label}
                                                </span>
                                            </div>
                                            <div
                                                className="embed-integrations-table-cell embed-integrations-table-cell__action"
                                                role="cell"
                                            >
                                                <Tooltip title="Edit Integration">
                                                    <span>
                                                        <IconButton
                                                            className="embed-integrations-action-button"
                                                            aria-label={`Edit Integration ${item.name}`}
                                                            onClick={() => openEdit(item)}
                                                        >
                                                            <SvgIcon
                                                                component={EditIcon}
                                                                inheritViewBox
                                                            />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                {item.linkEnabled && (
                                                    <Tooltip title="Open link">
                                                        <span>
                                                            <IconButton
                                                                className="embed-integrations-action-button"
                                                                aria-label={`Open link for ${item.name}`}
                                                                onClick={() =>
                                                                    window.open(
                                                                        item.linkUrl,
                                                                        "_blank",
                                                                        "noopener,noreferrer"
                                                                    )
                                                                }
                                                            >
                                                                <OpenInNewIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Delete Integration">
                                                    <span>
                                                        <IconButton
                                                            className="embed-integrations-action-button"
                                                            aria-label={`Delete Integration ${item.name}`}
                                                            onClick={() => setDeleteTarget(item)}
                                                        >
                                                            <SvgIcon
                                                                component={DeleteIcon}
                                                                inheritViewBox
                                                            />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <LoaderView show={isLoading} />
                </div>
            </div>

            {createOpen && companyId && (
                <IntegrationCreateModal
                    open={createOpen}
                    companyId={companyId}
                    knowledgeBases={knowledgeBases}
                    onCancel={() => setCreateOpen(false)}
                    onCreated={handleCreated}
                />
            )}
            {editTarget && companyId && (
                <IntegrationEditModal
                    open={!!editTarget}
                    companyId={companyId}
                    integration={editTarget}
                    knowledgeBases={knowledgeBases}
                    initialTabKey={editInitialTab}
                    onClose={() => setEditTarget(null)}
                    onUpdated={upsert}
                    onDeleteRequest={() => setDeleteTarget(editTarget)}
                />
            )}
            <ConfirmationPopup
                open={!!deleteTarget}
                type={ConfirmationPopupType.dangerous}
                title="Delete Integration?"
                description={`"${deleteTarget?.name ?? ""}" will be removed along with its provider/secret configuration. This cannot be undone.`}
                actionName="Delete"
                showCancelButton
                handleAction={handleDelete}
                handleClose={() => setDeleteTarget(null)}
            />
            <Toast />
        </HeaderLayout>
    );
}
